import { useFlags } from 'launchdarkly-react-client-sdk';
import { isLocal } from '../../../util/environment';
import { MaggieFeatureFlags } from '../../../util/feature-flags';

/**
 * Single gating site for the Entity Management module.
 *
 * Gated on the LaunchDarkly flag, which is what the back end uses:
 * `FLAG_KEYS.LEGAL_STRUCTURE_ENABLED = 'legalStructureEnabled'`, evaluated per tenant
 * via `launchDarklyService.variationForTenant`. Same flag key both sides.
 *
 * An earlier version also required a `Legal_Structure_Enabled` client config row,
 * following FD-4367's description. No such key exists in the API — the implementation
 * went with LaunchDarkly — so that condition could never be satisfied and the module
 * would never have appeared.
 *
 * Note the BE gate covers the mutation controllers (entityRelationship, legalEntity,
 * proposals). The reads under `structure.controller` carry only AuthGuard, so this
 * flag is the only thing hiding the module.
 *
 * TODO(FD-4464): add the module permission check here once BE defines the scope.
 */
export function useEntityManagementEnabled(): boolean {
  const { legalStructureEnabled } = useFlags<MaggieFeatureFlags>();

  // Forced on in local development so the module can be worked on without the flag
  // being set for the tenant. `isLocal()` is `import.meta.env.MODE === 'development'`,
  // a build-mode constant, so this branch does not exist in a production build.
  if (isLocal()) {
    return true;
  }

  return Boolean(legalStructureEnabled);
}
